<template>
  <div v-if="hasAny" ref="dockEl" class="dock">
    <UiPanel v-if="castingActionResolved" class="dock-card" tone="social" raised>
      <div class="dock-head">
        <div class="dock-title">
          <span class="dock-icon" aria-hidden="true">{{ ICONS.combat.magic }}</span>
          <div style="min-width: 0;">
            <div class="dock-name">{{ castingActionResolved.spellName }}</div>
            <div class="dock-sub text-muted">
              <span>Casting</span>
              <UiChip v-if="castingActionResolved.isPaused" variant="paused">Paused</UiChip>
            </div>
          </div>
        </div>

        <div class="dock-actions">
          <UiButton size="sm" variant="secondary" @click="emit('open-session', 'magic')">Open</UiButton>
          <UiButton
            v-if="castingActionResolved.isPaused"
            size="sm"
            variant="secondary"
            @click="emit('resume-casting')"
          >
            Resume
          </UiButton>
          <UiButton size="sm" variant="danger" @click="emit('cancel-casting')">Cancel</UiButton>
        </div>
      </div>

      <UiProgressBar :value="castingProgressPct" tone="social" />
      <div class="dock-foot text-muted stat-mono num">{{ castingRemainingLabel }}</div>
    </UiPanel>

    <UiPanel v-if="processingActionResolved" class="dock-card" tone="work" raised>
      <div class="dock-head">
        <div class="dock-title">
          <span class="dock-icon" aria-hidden="true">{{ ICONS.sessions.workshop }}</span>
          <div style="min-width: 0;">
            <div class="dock-name">{{ processingActionResolved.recipeName }}</div>
            <div class="dock-sub text-muted">
              <span class="stat-mono num">{{ formatNumber(doneCount) }}/{{ formatNumber(processingActionResolved.total) }} done</span>
              <UiChip v-if="processingActionResolved.isPaused" variant="paused">Paused</UiChip>
            </div>
          </div>
        </div>

        <div class="dock-actions">
          <UiButton size="sm" variant="secondary" @click="emit('open-session', 'workshop')">Open</UiButton>
          <UiButton
            v-if="!processingActionResolved.isPaused"
            size="sm"
            variant="secondary"
            @click="emit('pause-processing')"
          >
            Pause
          </UiButton>
          <UiButton
            v-else
            size="sm"
            variant="secondary"
            @click="emit('resume-processing')"
          >
            Resume
          </UiButton>
          <UiButton size="sm" variant="danger" @click="emit('cancel-processing')">Cancel</UiButton>
        </div>
      </div>

      <UiProgressBar :value="processingUnitProgressPct" tone="work" />
      <div class="dock-foot text-muted">
        <span class="stat-mono num">{{ processingRemainingLabel }}</span>
        <span v-if="processingActionResolved.durationMs" class="stat-mono num">
          • {{ formatMs(Math.max(0, Math.round(Number(processingActionResolved.durationMs) - processingProgressMs))) }} / unit
        </span>
      </div>
      <!-- Inputs consumed per unit, so players can tell "will this run out?" without leaving the dock. -->
      <div v-if="processingInputs.length" class="dock-inputs">
        <UiChip
          v-for="input in processingInputs"
          :key="input.itemId"
          :variant="input.short ? 'warning' : 'muted'"
          class="stat-mono num"
        >
          {{ input.name }}: {{ formatNumber(input.have) }} left
        </UiChip>
      </div>
    </UiPanel>

    <UiPanel v-if="gatheringActionResolved" class="dock-card" tone="work" raised>
      <div class="dock-head">
        <div class="dock-title">
          <span class="dock-icon" aria-hidden="true">{{ skillIcon(gatheringActionResolved.skillId) }}</span>
          <div style="min-width: 0;">
            <div class="dock-name">{{ gatheringNodeName }}</div>
            <div class="dock-sub text-muted">
              <span>{{ skillLabel(gatheringActionResolved.skillId) }}</span>
              <UiChip v-if="gatheringNodeDepleted" variant="warning">Depleted</UiChip>
            </div>
          </div>
        </div>

        <div class="dock-actions">
          <UiButton size="sm" variant="danger" @click="emit('stop-gathering')">Stop</UiButton>
        </div>
      </div>

      <UiProgressBar
        :value="gatheringNodeDepleted ? 100 : gatheringTickProgressPct"
        :tone="gatheringNodeDepleted ? 'warning' : 'work'"
      />
      <div class="dock-foot text-muted stat-mono num">
        {{ gatheringNodeDepleted ? "Depleted - waiting to respawn" : `Next in ${gatheringMsToNextTickLabel}` }}
      </div>
      <!-- Some nodes (e.g. fishing) consume a per-tick item (bait) from inventory; surface it the same way as processing inputs. -->
      <div v-if="gatheringRequiredItem" class="dock-inputs">
        <UiChip :variant="gatheringRequiredItem.have <= 0 ? 'warning' : 'muted'" class="stat-mono num">
          {{ gatheringRequiredItem.name }}: {{ formatNumber(gatheringRequiredItem.have) }} left
        </UiChip>
      </div>
    </UiPanel>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";
import type {
  CastingAction,
  GatheringAction,
  InventoryEntry,
  ProcessingAction,
  Recipe,
  ResourceNode
} from "@idle-rpg/shared";
import { ICONS } from "../lib/icons";
import { formatMs, formatNumber } from "../lib/format";
import { skillIcon, skillLabel } from "../lib/skillMeta";
import UiButton from "./ui/UiButton.vue";
import UiChip from "./ui/UiChip.vue";
import UiPanel from "./ui/UiPanel.vue";
import UiProgressBar from "./ui/UiProgressBar.vue";

type SessionId = "area" | "workshop" | "magic";

const props = defineProps<{
  processingAction?: ProcessingAction | null;
  gatheringAction?: GatheringAction | null;
  castingAction?: CastingAction | null;
  resourceNodes?: ResourceNode[];
  nodeDepleted?: boolean;
  /** Recipe backing the active processing action, used to list its consumed inputs. */
  processingRecipe?: Recipe | null;
  /** Needed to show how much of each consumed input (recipe ingredients, bait) remains. */
  inventory?: InventoryEntry[];
}>();

const emit = defineEmits<{
  (e: "open-session", session: SessionId): void;
  (e: "pause-processing"): void;
  (e: "resume-processing"): void;
  (e: "cancel-processing"): void;
  (e: "stop-gathering"): void;
  (e: "resume-casting"): void;
  (e: "cancel-casting"): void;
}>();

const dockEl = ref<HTMLElement | null>(null);
const nowMs = ref(Date.now());
let nowTimer: number | null = null;
let dockResizeObserver: ResizeObserver | null = null;
let toastResizeObserver: ResizeObserver | null = null;

const hasAny = computed(() => !!(props.processingAction || props.gatheringAction || props.castingAction));

const processingActionResolved = computed(() => props.processingAction ?? null);
const gatheringActionResolved = computed(() => props.gatheringAction ?? null);
const castingActionResolved = computed(() => props.castingAction ?? null);
const resourceNodesResolved = computed(() => props.resourceNodes ?? []);
const processingRecipeResolved = computed(() => props.processingRecipe ?? null);

// Same idiom as ProcessingWorkbench's inventoryQtyByItemId: sum stacked quantities per itemId once,
// then look them up per input instead of re-scanning the inventory for every row.
const inventoryQtyByItemId = computed(() => {
  const map = new Map<string, number>();
  for (const entry of props.inventory ?? []) {
    map.set(entry.itemId, (map.get(entry.itemId) ?? 0) + Math.floor(Number(entry.quantity || 0)));
  }
  return map;
});

function haveItemQty(itemId: string): number {
  return inventoryQtyByItemId.value.get(itemId) ?? 0;
}

function setToastDockOffsetPx(px: number) {
  const value = px > 0 ? `${Math.ceil(px)}px` : "0px";
  document.documentElement.style.setProperty("--toast-dock-offset", value);
  document.body?.style?.setProperty("--toast-dock-offset", value);
}

function updateToastOffset() {
  const dockRect = dockEl.value?.getBoundingClientRect() ?? null;
  const toastEl = document.querySelector(".toast-container") as HTMLElement | null;
  if (!dockRect || !toastEl) {
    setToastDockOffsetPx(0);
    return;
  }
  const toastRect = toastEl.getBoundingClientRect();
  const overlapX = dockRect.left < toastRect.right && dockRect.right > toastRect.left;
  const overlapY = dockRect.top < toastRect.bottom && dockRect.bottom > toastRect.top;
  if (!overlapX || !overlapY) {
    setToastDockOffsetPx(0);
    return;
  }
  setToastDockOffsetPx(dockRect.height > 0 ? dockRect.height + 16 : 0);
}

function observeDockSize() {
  if (dockResizeObserver) {
    dockResizeObserver.disconnect();
    dockResizeObserver = null;
  }
  if (toastResizeObserver) {
    toastResizeObserver.disconnect();
    toastResizeObserver = null;
  }
  if (typeof ResizeObserver === "undefined") return;
  if (!dockEl.value) return;
  dockResizeObserver = new ResizeObserver(() => updateToastOffset());
  dockResizeObserver.observe(dockEl.value);
  const toastEl = document.querySelector(".toast-container") as HTMLElement | null;
  if (toastEl) {
    toastResizeObserver = new ResizeObserver(() => updateToastOffset());
    toastResizeObserver.observe(toastEl);
  }
}

watch(
  () => hasAny.value,
  async (visible) => {
    await nextTick();
    if (!visible) {
      setToastDockOffsetPx(0);
      dockResizeObserver?.disconnect();
      dockResizeObserver = null;
      toastResizeObserver?.disconnect();
      toastResizeObserver = null;
      window.removeEventListener("resize", updateToastOffset);
      return;
    }
    observeDockSize();
    updateToastOffset();
    window.addEventListener("resize", updateToastOffset);
  },
  { immediate: true }
);

onMounted(() => {
  nowTimer = window.setInterval(() => {
    nowMs.value = Date.now();
  }, 250);
});

onBeforeUnmount(() => {
  if (nowTimer) window.clearInterval(nowTimer);
  dockResizeObserver?.disconnect();
  dockResizeObserver = null;
  toastResizeObserver?.disconnect();
  toastResizeObserver = null;
  window.removeEventListener("resize", updateToastOffset);
  setToastDockOffsetPx(0);
});

const doneCount = computed(() => {
  const action = processingActionResolved.value;
  if (!action) return 0;
  return Math.max(0, Math.floor(Number(action.total || 0) - Number(action.remaining || 0)));
});

const processingProgressBaseMs = ref(0);
const processingProgressBaseAtMs = ref(Date.now());

watch(
  () => [processingActionResolved.value?.id, processingActionResolved.value?.progressMs, processingActionResolved.value?.isPaused] as const,
  () => {
    const action = processingActionResolved.value;
    if (!action) {
      processingProgressBaseMs.value = 0;
      processingProgressBaseAtMs.value = Date.now();
      return;
    }
    processingProgressBaseMs.value = Math.max(0, Math.floor(Number(action.progressMs || 0)));
    processingProgressBaseAtMs.value = Date.now();
  },
  { immediate: true }
);

const processingProgressMs = computed(() => {
  const action = processingActionResolved.value;
  if (!action) return 0;
  const base = processingProgressBaseMs.value;
  if (action.isPaused) return base;
  return Math.max(0, base + (nowMs.value - processingProgressBaseAtMs.value));
});

const processingUnitProgressPct = computed(() => {
  const action = processingActionResolved.value;
  if (!action) return 0;
  const denom = Math.max(1, Math.floor(Number(action.durationMs || 1)));
  const progress = Math.min(denom, Math.max(0, processingProgressMs.value));
  return Math.max(0, Math.min(100, (progress / denom) * 100));
});

const processingRemainingLabel = computed(() => {
  const action = processingActionResolved.value;
  if (!action) return "";
  const remaining = Math.max(0, Math.floor(Number(action.remaining || 0)));
  if (remaining <= 0) return "Completed";
  if (remaining === 1) return "1 unit left";
  return `${formatNumber(remaining)} units left`;
});

// "warning" (short) mirrors ProcessingWorkbench's picker: not enough on hand for even the *next*
// unit, which is the point at which the run will actually stall.
const processingInputs = computed(() => {
  const recipe = processingRecipeResolved.value;
  if (!recipe) return [];
  return recipe.inputs.map((input) => {
    const have = haveItemQty(input.itemId);
    const perUnit = Math.max(1, Math.floor(Number(input.quantity || 1)));
    return {
      itemId: input.itemId,
      name: input.itemName || input.itemId,
      have,
      short: have < perUnit
    };
  });
});

const gatheringNodeDepleted = computed(() => props.nodeDepleted ?? false);

const gatheringNodeName = computed(() => {
  const action = gatheringActionResolved.value;
  if (!action) return "Gathering";
  const node = resourceNodesResolved.value.find((n) => n.id === action.nodeId);
  return node?.name || "Gathering";
});

// Only nodes checked "inventory" (e.g. bait for fishing) actually consume the item per tick;
// "equipped" tools aren't drawn down, so there's nothing to run out of.
const gatheringRequiredItem = computed(() => {
  const action = gatheringActionResolved.value;
  if (!action) return null;
  const node = resourceNodesResolved.value.find((n) => n.id === action.nodeId);
  if (!node?.requiredItemId || node.requiredItemMode !== "inventory") return null;
  return {
    itemId: node.requiredItemId,
    name: node.requiredItemName || node.requiredItemId,
    have: haveItemQty(node.requiredItemId)
  };
});

const gatheringTickProgressPct = computed(() => {
  const action = gatheringActionResolved.value;
  if (!action) return 0;
  const last = Date.parse(action.lastResolvedAt || action.startedAt);
  const elapsed = Math.max(0, nowMs.value - (Number.isFinite(last) ? last : nowMs.value));
  const denom = Math.max(1, Number(action.intervalMs || 1));
  const phase = elapsed % denom;
  return Math.max(0, Math.min(100, (phase / denom) * 100));
});

const gatheringMsToNextTickLabel = computed(() => {
  const action = gatheringActionResolved.value;
  if (!action) return "";
  const last = Date.parse(action.lastResolvedAt || action.startedAt);
  const elapsed = Math.max(0, nowMs.value - (Number.isFinite(last) ? last : nowMs.value));
  const denom = Math.max(1, Number(action.intervalMs || 1));
  const remaining = Math.max(0, denom - (elapsed % denom));
  return formatMs(remaining);
});

const castingRemainingLabel = computed(() => {
  const action = castingActionResolved.value;
  if (!action) return "";
  if (action.isPaused) return "Paused";
  const completes = Date.parse(action.completesAt);
  if (!Number.isFinite(completes)) return "—";
  return formatMs(Math.max(0, completes - nowMs.value));
});

const castingProgressPct = computed(() => {
  const action = castingActionResolved.value;
  if (!action) return 0;
  const started = Date.parse(action.startedAt);
  const completes = Date.parse(action.completesAt);
  const denom = Math.max(1, Number.isFinite(started) && Number.isFinite(completes) ? completes - started : 1);
  const elapsed = Math.max(0, Math.min(denom, nowMs.value - (Number.isFinite(started) ? started : nowMs.value)));
  return Math.max(0, Math.min(100, (elapsed / denom) * 100));
});

</script>

<style scoped>
.dock {
  position: fixed;
  bottom: calc(env(safe-area-inset-bottom) + 1.5rem);
  right: var(--dock-right, 1.5rem);
  width: min(420px, calc(100vw - var(--shell-rail, 0px) - var(--dock-right, 0px) - 3rem));
  z-index: 3660;
  display: grid;
  gap: 0.75rem;
  pointer-events: none;
}

.dock-card {
  display: grid;
  gap: 0.65rem;
  pointer-events: auto;
  container-type: inline-size;
}

.dock-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.dock-title {
  display: flex;
  align-items: flex-start;
  gap: 0.7rem;
  min-width: 0;
}

.dock-icon {
  width: 38px;
  height: 38px;
  border-radius: var(--radius-2);
  display: grid;
  place-items: center;
  background: var(--glass-1);
  border: 1px solid var(--glass-border-1);
  flex: 0 0 auto;
}

.dock-name {
  font-weight: 800;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100%;
}

.dock-sub {
  margin-top: 0.15rem;
  display: inline-flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  align-items: center;
  font-size: 0.9rem;
}

.dock-actions {
  display: inline-flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  justify-content: flex-end;
  align-items: center;
}

.dock-foot {
  font-size: 0.9rem;
}

.dock-inputs {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
}

@media (max-width: 900px) {
  .dock {
    left: 50%;
    right: auto;
    transform: translateX(-50%);
    width: min(420px, calc(100vw - 2rem));
    bottom: calc(env(safe-area-inset-bottom) + 5.75rem);
  }
}

@container (max-width: 360px) {
  .dock-head {
    flex-direction: column;
    align-items: stretch;
  }

  .dock-actions {
    width: 100%;
    justify-content: flex-start;
  }
}
</style>
